import { inject, Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ConsentService } from './consent.service';

/** Meta-Standardereignisse, die wir tatsaechlich nutzen. */
export type MetaPixelEvent =
  | 'PageView'
  | 'ViewContent'
  | 'AddToCart'
  | 'InitiateCheckout'
  | 'CompleteRegistration';

declare global {
  interface Window {
    fbq?: any;
    _fbq?: any;
  }
}

/**
 * Meta-Browser-Pixel fuer Top- und Mid-Funnel.
 *
 * Bewusst NICHT fuer Purchase: das feuert serverseitig aus dem Stripe-Webhook.
 * Damit ist es consent-unabhaengig, adblock-resistent und ueberlebt einen
 * geschlossenen Tab nach der Stripe-Rueckleitung. Und weil es nur eine Quelle
 * gibt, entfaellt jedes Dedup-Problem zwischen Browser und Server.
 *
 * Der Pixel wird erst nach erteilter Einwilligung geladen. Vorher existiert
 * `fbq` nicht und es wird kein Cookie gesetzt - genau das verlangt TDDDG §25.
 * Fuer Meta gibt es keinen EU-taugliche "Limited"-Modus; das einzig korrekte
 * Muster ist: gar nicht laden.
 */
@Injectable({
  providedIn: 'root',
})
export class MetaPixelService {
  private consentService = inject(ConsentService);

  private loaded = false;
  /** Ereignisse, die vor der Einwilligung anfielen - werden verworfen, nicht nachgeholt. */
  private droppedBeforeConsent = 0;

  /**
   * Wird aus app.component aufgerufen und reagiert auf jede Aenderung der
   * Einwilligung: laden bei Zustimmung, nichts tun bei Ablehnung.
   */
  init(): void {
    if (!environment.metaPixelId) {
      return;
    }

    this.consentService.decision$.subscribe((decision) => {
      if (decision === 'granted') {
        this.load();
      }
    });
  }

  track(event: MetaPixelEvent, params?: Record<string, unknown>): void {
    if (!this.isActive()) {
      this.droppedBeforeConsent++;
      return;
    }
    try {
      window.fbq?.('track', event, params ?? {});
    } catch (e) {
      console.warn('MetaPixel track fehlgeschlagen', event, e);
    }
  }

  /** Fuer Ereignisse ohne Meta-Standardnamen, z.B. "Formular begonnen". */
  trackCustom(name: string, params?: Record<string, unknown>): void {
    if (!this.isActive()) {
      this.droppedBeforeConsent++;
      return;
    }
    try {
      window.fbq?.('trackCustom', name, params ?? {});
    } catch (e) {
      console.warn('MetaPixel trackCustom fehlgeschlagen', name, e);
    }
  }

  get skippedEventCount(): number {
    return this.droppedBeforeConsent;
  }

  private isActive(): boolean {
    return (
      this.loaded &&
      !!environment.metaPixelId &&
      this.consentService.hasMarketingConsent()
    );
  }

  /**
   * Laedt fbevents.js nach. Idempotent - mehrfache Aufrufe sind harmlos, etwa
   * wenn der Nutzer die Einwilligung widerruft und erneut erteilt.
   */
  private load(): void {
    if (this.loaded || !environment.metaPixelId) {
      return;
    }

    try {
      // Offizieller Meta-Loader, von der inline-Variante auf TypeScript gebracht.
      const n: any = (window.fbq = function (...args: unknown[]) {
        n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
      });
      if (!window._fbq) {
        window._fbq = n;
      }
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];

      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(script);

      this.loaded = true;

      window.fbq('init', environment.metaPixelId);
      window.fbq('track', 'PageView');
    } catch (e) {
      console.error('MetaPixel konnte nicht geladen werden', e);
      this.loaded = false;
    }
  }
}
